import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  NEWSLETTER_SUBSCRIBE_ACTION,
  loadTurnstile,
  newsletterSubscribeUrl
} from '../utils/turnstileWidget';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VERIFICATION_ERROR = "We couldn't verify your submission. Please try again.";
const GENERIC_ERROR = 'Something went wrong. Please try again.';
const TURNSTILE_SITE_KEY = String(import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim();

const NewsletterPage = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [widgetAttempt, setWidgetAttempt] = useState(0);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const resettingRef = useRef(false);

  const setToken = (value: string | null) => {
    tokenRef.current = value;
  };

  const resetTurnstile = useCallback(() => {
    setToken(null);
    const widgetId = widgetIdRef.current;
    if (!widgetId || !window.turnstile || resettingRef.current) return;
    resettingRef.current = true;
    window.turnstile.reset(widgetId);
    window.setTimeout(() => {
      resettingRef.current = false;
    }, 0);
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Join the Traffic Lite Insider';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      console.warn('newsletter: VITE_TURNSTILE_SITE_KEY is not configured');
      return;
    }

    let cancelled = false;
    const container = turnstileRef.current;

    const renderWidget = async () => {
      try {
        const turnstile = await loadTurnstile();
        if (cancelled || !container) return;
        let rendered = false;
        const renderChallenge = () => {
          if (rendered || cancelled || !container) return;
          rendered = true;
          widgetIdRef.current = turnstile.render(container, {
            sitekey: TURNSTILE_SITE_KEY,
            action: NEWSLETTER_SUBSCRIBE_ACTION,
            appearance: 'interaction-only',
            theme: 'light',
            size: 'normal',
            execution: 'render',
            callback: (token) => {
              if (cancelled) return;
              setToken(token);
            },
            'expired-callback': () => {
              if (cancelled) return;
              resetTurnstile();
            },
            'timeout-callback': () => {
              if (cancelled) return;
              resetTurnstile();
            },
            'error-callback': () => {
              if (cancelled) return;
              setToken(null);
              setError(VERIFICATION_ERROR);
            }
          });
        };
        try {
          turnstile.ready(renderChallenge);
        } catch {
          renderChallenge();
        }
      } catch {
        if (!cancelled) setError(VERIFICATION_ERROR);
      }
    };

    renderWidget();

    return () => {
      cancelled = true;
      const widgetId = widgetIdRef.current;
      widgetIdRef.current = null;
      setToken(null);
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [widgetAttempt, resetTurnstile]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const emailValue = email.trim().toLowerCase();

    if (!emailValue || !EMAIL_PATTERN.test(emailValue)) {
      setError('Please enter a valid email address.');
      return;
    }

    const turnstileToken = tokenRef.current;
    if (!TURNSTILE_SITE_KEY || !turnstileToken || !widgetIdRef.current) {
      setError(VERIFICATION_ERROR);
      if (widgetIdRef.current) {
        resetTurnstile();
      } else {
        setWidgetAttempt((attempt) => attempt + 1);
      }
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(newsletterSubscribeUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValue, turnstileToken })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success !== true) {
        const serverError = typeof data?.error === 'string' ? data.error : '';
        setError(serverError === VERIFICATION_ERROR ? VERIFICATION_ERROR : GENERIC_ERROR);
        resetTurnstile();
        return;
      }

      setSucceeded(true);
    } catch {
      setError(GENERIC_ERROR);
      resetTurnstile();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto flex h-16 w-16 items-center justify-center">
            <img
              src="/icon.png"
              alt="Traffic Lite"
              className="h-16 w-16 object-contain"
            />
          </div>
          <p className="text-sm tracking-[0.2em]" aria-hidden="true">
            🔴 🟡 🟢
          </p>
          <h1 className="traffic-heading-font text-3xl font-bold text-gray-900">
            Join the Traffic Lite Insider
          </h1>
          <p className="text-base text-gray-600 leading-relaxed">
            Occasional updates on what I'm building, what I'm learning from educators,
            and where Traffic Lite is headed next.
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {succeeded ? (
            <div className="text-center space-y-3 py-2" role="status" aria-live="polite">
              <p className="traffic-heading-font text-2xl font-bold text-gray-900">
                You're in! 🚦
              </p>
              <p className="text-base text-gray-600 leading-relaxed">
                Thanks for following along. The next Traffic Lite Insider will land in your
                inbox.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label htmlFor="newsletter-email" className="sr-only">
                  Your email address
                </label>
                <Input
                  id="newsletter-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (error) setError(null);
                  }}
                  disabled={submitting}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'newsletter-error' : undefined}
                  className="h-12 text-base"
                />
                <div ref={turnstileRef} id="newsletter-turnstile" />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                size="lg"
              >
                {submitting ? 'Joining...' : 'Keep Me in the Loop →'}
              </Button>

              {error && (
                <p id="newsletter-error" className="text-sm !text-red-600" role="alert">
                  {error}
                </p>
              )}

              <p className="text-sm text-gray-500 text-center leading-relaxed">
                No spam. Just occasional Traffic Lite updates and ideas for teaching and
                learning in an AI-powered world.
              </p>
            </form>
          )}

          <div className="text-center pt-2 border-t">
            <a
              href="https://www.trafficliteedu.com"
              className="text-sm text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline"
            >
              trafficliteedu.com
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewsletterPage;
